// Booking arithmetic shared by the assistant profile and the booking modal.
//
// The platform sells an assistant's time in fixed 12-hour blocks. A booking
// starts on a 30-minute boundary and runs a whole number of blocks; the
// assistant is then unavailable for a return buffer before the next booking
// can start. These helpers mirror the server's rules in
// pulo_api/src/features/bookings/services.ts — the calendar drawn here is
// advisory, and the API re-checks every window on submit.

export const BLOCK_HOURS = 12;
export const START_INCREMENT_MINUTES = 30;
export const DEFAULT_RETURN_BUFFER_MINUTES = 60;

// No last-minute bookings: a trip must start at least this far out, giving the
// assistant time to accept and to hire escorts, security and vehicles. Mirrors
// config.booking.minLeadHours on the server, which rejects anything sooner.
export const MIN_LEAD_HOURS = 12;

const MS_PER_MINUTE = 60 * 1000;
export const BLOCK_MS = BLOCK_HOURS * 60 * MS_PER_MINUTE;

// Volume discount by blocks. Mirrors getPricingMultiplier() on the server;
// the thresholds are the old day-based tiers restated in 12-hour blocks.
export const getBlockDiscountRate = (blocks) => {
  const parsed = Number(blocks);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  if (parsed > 20) return 0.65;
  if (parsed > 10) return 0.8;
  if (parsed > 4) return 0.9;
  return 1;
};

// The earliest instant a trip may start. Feed this to the calendar helpers as
// their `now` and the whole lead-time rule falls out of the slot filtering they
// already do.
export const earliestBookableStart = (now = new Date()) =>
  new Date(now.getTime() + MIN_LEAD_HOURS * 60 * MS_PER_MINUTE);

export const blocksToMs = (blocks) => Math.max(1, Number(blocks) || 1) * BLOCK_MS;

export const addBlocks = (start, blocks) =>
  start ? new Date(start.getTime() + blocksToMs(blocks)) : null;

// Whole blocks between two instants, for reading an existing draft back.
export const countBlocks = (start, end) => {
  if (!start || !end) return 1;
  const blocks = Math.round((end.getTime() - start.getTime()) / BLOCK_MS);
  return blocks < 1 ? 1 : blocks;
};

// Busy windows arrive from the API as ISO strings with the assistant's return
// buffer already folded into `end`, so nothing here needs to re-apply it to
// existing bookings.
export const parseBusyWindows = (busy = []) =>
  (Array.isArray(busy) ? busy : [])
    .map((window) => ({
      start: new Date(window?.start),
      end: new Date(window?.end),
    }))
    .filter(
      (window) =>
        !Number.isNaN(window.start.getTime()) && !Number.isNaN(window.end.getTime())
    );

// Can a booking of `blocks` blocks start here? The window it would claim runs
// to its departure plus its own trailing buffer, so a later booking can't be
// squeezed into the dead time this one creates.
export const isStartAvailable = ({
  start,
  blocks,
  busyWindows = [],
  returnBufferMinutes = DEFAULT_RETURN_BUFFER_MINUTES,
  now = new Date(),
}) => {
  if (!start) return false;
  if (start.getTime() < now.getTime()) return false;

  const end = new Date(
    start.getTime() + blocksToMs(blocks) + returnBufferMinutes * MS_PER_MINUTE
  );

  return !busyWindows.some(
    (window) => start.getTime() < window.end.getTime() && window.start.getTime() < end.getTime()
  );
};

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

// Every 30-minute start on `date` at which a booking of this length still
// fits. Feeds the time picker, and an empty result means the whole day is
// unbookable and the calendar should grey it out.
export const getAvailableStartTimes = ({
  date,
  blocks,
  busyWindows = [],
  returnBufferMinutes = DEFAULT_RETURN_BUFFER_MINUTES,
  now = new Date(),
}) => {
  if (!date) return [];

  const slots = [];
  const dayStart = startOfDay(date);
  const slotCount = (24 * 60) / START_INCREMENT_MINUTES;

  for (let i = 0; i < slotCount; i += 1) {
    const slot = new Date(dayStart.getTime() + i * START_INCREMENT_MINUTES * MS_PER_MINUTE);
    if (isStartAvailable({ start: slot, blocks, busyWindows, returnBufferMinutes, now })) {
      slots.push(slot);
    }
  }

  return slots;
};

export const isDayFullyBooked = ({
  date,
  blocks,
  busyWindows = [],
  returnBufferMinutes = DEFAULT_RETURN_BUFFER_MINUTES,
  now = new Date(),
}) =>
  getAvailableStartTimes({ date, blocks, busyWindows, returnBufferMinutes, now }).length === 0;

// Days in [from, to] with no bookable start, for the calendar's excludeDates.
// Past days are left out — minDate already handles those.
export const getFullyBookedDays = ({
  from,
  to,
  blocks,
  busyWindows = [],
  returnBufferMinutes = DEFAULT_RETURN_BUFFER_MINUTES,
  now = new Date(),
}) => {
  if (!from || !to || !busyWindows.length) return [];

  const days = [];
  const cursor = startOfDay(from);
  const last = startOfDay(to);

  while (cursor.getTime() <= last.getTime()) {
    const day = new Date(cursor);
    if (
      day.getTime() >= startOfDay(now).getTime() &&
      isDayFullyBooked({ date: day, blocks, busyWindows, returnBufferMinutes, now })
    ) {
      days.push(day);
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

export const formatBlockLabel = (blocks) => {
  const count = Math.max(1, Number(blocks) || 1);
  const hours = count * BLOCK_HOURS;
  return count === 1 ? `${BLOCK_HOURS}hrs` : `${count} × ${BLOCK_HOURS}hrs (${hours}hrs)`;
};
